import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import findLastIndex from 'lodash/findLastIndex';
import findIndex from 'lodash/findIndex';

import { getStepsConfig } from '..';
import type { AppData } from '../../types/app_data';
import { AppStepSection } from '../../types/app_data';
import type { Step } from '../types/step';

// /**
//  * Get {@link STEPS} filtered only by selected service.
//  */
// export function getStepsBySelectedService(selectedService: SelectedServiceType): typeof STEPS {
//   const filtered = { ...STEPS };
//   for (const key of Object.keys(filtered)) {
//     const stepSection = key as unknown as AppStepSection;
//     filtered[stepSection] = filtered[stepSection].filter((step) => step.canShow({ stepSection, selectedService }));
//   }
//   return filtered;
// }

/**
 * Hook to handle step progress calculation.
 */
export function useStepProgress(options: {
  stepID?: string;
  appData: AppData;
  onProgressChange: (allProgress: number, categoryProgress: number) => void;
}): void {
  const { onProgressChange } = options;
  const STEPS = useMemo(() => getStepsConfig(options.appData.flowType), [options.appData.flowType]);

  const [{ stepID, appData }, setStepID] = useState({
    stepID: options.stepID,
    appData: options.appData,
  });
  // Only react to appData changes when stepID changes.
  const appDataRef = useRef(options.appData);
  useEffect(() => {
    appDataRef.current = options.appData;
  }, [options.appData]);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setStepID({
        stepID: options.stepID,
        appData: appDataRef.current,
      });
    }, 100);
    return () => clearTimeout(timeout);
  }, [options.stepID]);

  const stepSection = appData.stepSection;

  const sectionAllSteps = STEPS[stepSection];

  const currentStep = useMemo(() => {
    return sectionAllSteps.find((step) => step.id === stepID);
  }, [sectionAllSteps, stepID]);

  const isStepExcluded = useCallback(
    (step: Step) => {
      return typeof step.excludeFromProgressCalculation === 'boolean'
        ? step.excludeFromProgressCalculation
        : typeof step.excludeFromProgressCalculation === 'function'
        ? step.excludeFromProgressCalculation(appData)
        : false;
    },
    [appData]
  );

  const sectionIncludedSteps = useMemo(() => {
    return sectionAllSteps.filter((step) => !isStepExcluded(step));
  }, [isStepExcluded, sectionAllSteps]);

  const shouldExcludeCurrentStep = useMemo(() => {
    if (!currentStep) {
      return true;
    }
    return isStepExcluded(currentStep);
  }, [currentStep, isStepExcluded]);

  const sectionProgress = useMemo(() => {
    if (shouldExcludeCurrentStep) {
      return undefined;
    }
    const index = sectionIncludedSteps.findIndex((step) => step.id === stepID);
    if (index === -1) {
      return undefined;
    }
    return index;
  }, [sectionIncludedSteps, shouldExcludeCurrentStep, stepID]);

  /**
   * The maximum percentage of the progress for the CreateAccount section.
   */
  const createAccountPercentMax = useMemo(() => {
    return 0.3;
  }, []);

  const allProgressPercent = useMemo(() => {
    if (shouldExcludeCurrentStep || !sectionProgress) {
      return;
    }
    const sectionPercent = sectionProgress / sectionIncludedSteps.length;
    switch (stepSection) {
      case AppStepSection.CreateAccount:
        return sectionPercent * createAccountPercentMax;
      case AppStepSection.Onboarding:
      case AppStepSection.Challenge:
        return createAccountPercentMax + sectionPercent * (1 - createAccountPercentMax);
      default:
        return undefined;
    }
  }, [shouldExcludeCurrentStep, sectionProgress, sectionIncludedSteps.length, stepSection, createAccountPercentMax]);

  const categoryProgressPercent = useMemo(() => {
    if (
      !currentStep ||
      shouldExcludeCurrentStep ||
      ![AppStepSection.CreateAccount, AppStepSection.Onboarding, AppStepSection.Challenge].includes(stepSection)
    ) {
      return undefined;
    }
    const currentCategory = currentStep.category?.(appData);
    if (!currentCategory) {
      return undefined;
    }

    const steps: Step[] =
      stepSection === AppStepSection.Challenge
        ? [...STEPS[AppStepSection.CreateAccount], ...STEPS[AppStepSection.Challenge]]
        : [...STEPS[AppStepSection.CreateAccount], ...STEPS[AppStepSection.Onboarding]];

    // Find the start and end index of the current category in `steps`. Same category could appear both in
    // CreateAccount and Onboarding/Challenge sections. So we need to handle the category at the intersection.
    const categoryStartIndex =
      stepSection === AppStepSection.CreateAccount
        ? STEPS[AppStepSection.CreateAccount].findIndex((step) => step.category?.(appData) === currentCategory)
        : // If the current category is the first category in the Onboarding/Challenge section, then the start index
        // could be in the CreateAccount section. So find from the end of the CreateAccount section.
        currentCategory === STEPS[stepSection][0].category?.(appData)
        ? findLastIndex(STEPS[AppStepSection.CreateAccount], (step) => step.category?.(appData) !== currentCategory) + 1
        : // Otherwise, find from the end of the CreateAccount section.
          findIndex(
            steps,
            (step) => step.category?.(appData) === currentCategory,
            STEPS[AppStepSection.CreateAccount].length
          );
    const categoryEndIndex =
      stepSection !== AppStepSection.CreateAccount
        ? findLastIndex(steps, (step) => step.category?.(appData) === currentCategory)
        : STEPS[AppStepSection.Onboarding].length > 0 &&
          currentCategory ===
            STEPS[AppStepSection.Onboarding][STEPS[AppStepSection.Onboarding].length - 1].category?.(appData)
        ? findIndex(steps, (step) => step.category?.(appData) !== currentCategory) - 1
        : findLastIndex(steps, (step) => step.category?.(appData) === currentCategory);

    if (categoryStartIndex === -1 || categoryEndIndex === -1) {
      return undefined;
    }

    const categorySteps = steps.slice(categoryStartIndex, categoryEndIndex + 1);
    const currentStepIndex = categorySteps.findIndex((step) => step.id === stepID);
    if (currentStepIndex === -1) {
      return undefined;
    }

    return (currentStepIndex + 1) / categorySteps.length;
  }, [appData, currentStep, shouldExcludeCurrentStep, stepID, stepSection, STEPS]);

  useEffect(() => {
    if (allProgressPercent !== undefined && categoryProgressPercent !== undefined) {
      onProgressChange(allProgressPercent, categoryProgressPercent);
    }
  }, [allProgressPercent, stepID, onProgressChange, categoryProgressPercent]);
}
